import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: 0 2em 0 2em;
  box-sizing: border-box;
  color: ${({ theme }) => theme.text};
  gap: 0;
  position: relative;
  flex: 1;
  min-height: 0;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * {
    margin: 0;
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 1em;
  }

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 14px;
    flex-shrink: 0;

    .header-icon { display: none; }

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "NOTIFICAÇÕES FISCAIS · E-CAC";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};
          background: transparent !important;
          padding: 0 !important;
          border: none !important;
          opacity: 1 !important;

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          ${mono};
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          font-size: 0.9em;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ========== KPI CARDS ========== */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 600px)  { grid-template-columns: 1fr; }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
      }

      .text p:first-child {
        ${mono};
        font-size: 1.6em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.02em;
        line-height: 1.1;
      }

      .text p:last-child {
        font-size: 0.72em;
        font-weight: 500;
        opacity: 0.5;
        margin-top: 2px;
      }
    }

    &.success .text p:first-child { color: ${tokens.brand}; }
    &.warning .text p:first-child { color: ${tokens.warn}; }
    &.danger  .text p:first-child { color: ${tokens.danger}; }

    &.success .thumb { color: ${tokens.brand};  opacity: 0.65; }
    &.warning .thumb { color: ${tokens.warn};   opacity: 0.65; }
    &.danger  .thumb { color: ${tokens.danger};  opacity: 0.65; }
  }

  /* ========== TOOLBAR ========== */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;
    flex-wrap: wrap;
  }

  .toolbar-right {
    ${({ theme }) => cardBase(theme)};
    padding: 3px;
    display: flex;
    gap: 4px;
  }

  .search-box {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 12px;
    ${({ theme }) => cardBase(theme)};
    flex: 1;
    max-width: 360px;

    &:focus-within { border-color: ${tokens.brandRing}; }

    svg {
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      font-size: 1em;
    }

    input {
      background: transparent;
      border: none;
      outline: none;
      color: ${({ theme }) => theme.text};
      font-size: 0.85em;
      width: 100%;

      &::placeholder {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
      }
    }
  }

  .filter-pill {
    ${mono};
    padding: 6px 12px;
    background: transparent;
    border: none;
    border-radius: ${tokens.radiusSm};
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
    font-size: 0.66em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    &:hover { opacity: 0.9; }
    &.active {
      background: ${tokens.brandSoft};
      color: ${tokens.brand};
      opacity: 1;
    }
  }

  /* ========== NOTIFICATION LIST (card grid) ========== */
  .notif-grid {
    display: flex;
    flex-direction: column;
    gap: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  .notif-card {
    ${({ theme }) => cardBase(theme)};
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 8px;
    cursor: pointer;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      bottom: 8px;
      width: 3px;
      border-radius: 0 3px 3px 0;
      opacity: 0.55;
      transition: opacity ${tokens.durBase} ${tokens.easeOut};
    }

    &.ok::before     { background: ${tokens.brand}; }
    &.warn::before   { background: ${tokens.warn}; }
    &.danger::before { background: ${tokens.danger}; }

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
      &::before { opacity: 1; }
    }

    .notif-icon {
      width: 40px;
      height: 40px;
      min-width: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: ${tokens.radiusMd};
      font-size: 1.1em;
    }

    .notif-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 0;

      .notif-title {
        margin: 0;
        font-size: 0.92em;
        font-weight: 500;
        letter-spacing: -0.01em;
        line-height: 1.25;
        color: ${({ theme }) => theme.text};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .notif-meta {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;

        span {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.78em;
          color: ${({ theme }) => theme.text};
          opacity: 0.55;

          svg { font-size: 1em; opacity: 0.6; }
        }
      }

      .notif-tags {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
      }
    }
  }

  /* Status pills */
  .status-pill {
    ${mono};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: ${tokens.radiusPill};
    font-size: 0.6em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border: 1px solid transparent;
    white-space: nowrap;

    &.ok {
      background: ${tokens.brandSoft};
      color: ${tokens.brand};
      border-color: ${tokens.brandRing};
    }
    &.warn {
      background: ${tokens.warnSoft};
      color: ${tokens.warn};
      border-color: rgba(245, 158, 11, 0.22);
    }
    &.danger {
      background: ${tokens.dangerSoft};
      color: ${tokens.danger};
      border-color: rgba(239, 68, 68, 0.22);
    }
  }

  /* Type tag (Notion-style) */
  .type-tag {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: ${tokens.radiusPill};
    font-family: ${tokens.fontSans};
    font-size: 0.66em;
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1.5;
    color: #fff;
    text-transform: capitalize;
    white-space: nowrap;
    border: none;
  }

  /* ========== EMPTY STATE ========== */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 110px 20px 80px;
    background: transparent;
    border: none;
    text-align: center;

    .empty-icon {
      width: 64px;
      height: 64px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: 50%;
      color: ${tokens.brand};
      opacity: 0.7;

      svg { font-size: 1.6em; }
    }

    h4 {
      margin: 0;
      font-size: 1em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
      max-width: 280px;
      line-height: 1.55;
    }
  }
`;

/* ========== LEGACY EXPORTS (mantidos para compatibilidade) ========== */
export const HeaderStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 14px;
  flex-shrink: 0;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
`;

export const StatCard = styled.div``;
export const StatIcon = styled.div``;
export const StatValue = styled.div``;
export const StatLabel = styled.div``;
export const Content = styled.div``;
export const Grid = styled.div``;
export const Card = styled.div``;
export const CardHeader = styled.div``;
export const CardTitle = styled.h3``;
export const CardBody = styled.div``;
export const IntimationList = styled.div``;
export const IntimationItem = styled.div``;
export const IntimationIcon = styled.div``;
export const IntimationInfo = styled.div``;
export const IntimationTitle = styled.h4``;
export const IntimationMeta = styled.div``;
export const IntimationBadge = styled.span``;
export const IntimationActions = styled.div``;
export const ActionButton = styled.button``;
export const EmptyState = styled.div``;
export const FilterBar = styled.div``;
export const FilterButton = styled.button``;
export const SearchInput = styled.div``;
